import { useMemo, useState } from "react";
import { parseImportFile } from "../../utils/importFileParser";

const STRATEGIES = [
  { value: "skip", label: "Dubbele overslaan" },
  { value: "update", label: "Dubbele bijwerken" },
  { value: "create", label: "Altijd proberen nieuw" },
];

const HEADER_MAP = {
  naam: ["naam", "name", "bedrijfsnaam", "company"],
  plaats: ["plaats", "city", "woonplaats"],
  email: ["email", "e-mail", "mail"],
  telefoon: ["telefoon", "phone", "mobile"],
  adres: ["adres", "address", "street"],
  postcode: ["postcode", "zip", "postalcode"],
  kvkNummer: ["kvk", "kvknummer", "chamberofcommerce"],
  btwNummer: ["btw", "btwnummer", "vat", "vatnumber"],
};

const HEADER_LIKE_VALUES = new Set([
  "bedrijfsnaam",
  "naam",
  "company",
  "companyname",
  "klantnummer",
  "status",
  "bezoekadres",
  "factuuradres",
  "kvk",
  "btw",
  "hoofdcontactpersoon",
  "telefoon",
  "email",
  "e-mail",
  "klanttype",
]);

function isHeaderLikeMappedRow(row) {
  const values = Object.values(row || {})
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  if (values.length === 0) return false;
  const matches = values.filter((value) => HEADER_LIKE_VALUES.has(value)).length;
  return matches >= 2;
}

function mapParsedRows(headers, rawRows) {
  const normalizedHeaders = headers.map((value) => String(value || "").trim().toLowerCase());
  const findHeader = (candidates) => {
    const idx = normalizedHeaders.findIndex((header) => candidates.includes(header));
    return idx >= 0 ? headers[idx] : "";
  };

  const selected = {
    naam: findHeader(HEADER_MAP.naam),
    plaats: findHeader(HEADER_MAP.plaats),
    email: findHeader(HEADER_MAP.email),
    telefoon: findHeader(HEADER_MAP.telefoon),
    adres: findHeader(HEADER_MAP.adres),
    postcode: findHeader(HEADER_MAP.postcode),
    kvkNummer: findHeader(HEADER_MAP.kvkNummer),
    btwNummer: findHeader(HEADER_MAP.btwNummer),
  };

  const rows = [];
  const errors = [];

  rawRows.forEach((row, index) => {
    const naam = selected.naam ? String(row[selected.naam] || "").trim() : "";
    const mapped = {
      naam,
      plaats: selected.plaats ? String(row[selected.plaats] || "").trim() : "",
      email: selected.email ? String(row[selected.email] || "").trim() : "",
      telefoon: selected.telefoon ? String(row[selected.telefoon] || "").trim() : "",
      adres: selected.adres ? String(row[selected.adres] || "").trim() : "",
      postcode: selected.postcode ? String(row[selected.postcode] || "").trim() : "",
      kvkNummer: selected.kvkNummer ? String(row[selected.kvkNummer] || "").trim() : "",
      btwNummer: selected.btwNummer ? String(row[selected.btwNummer] || "").trim() : "",
    };

    if (isHeaderLikeMappedRow(mapped)) {
      return;
    }

    if (!mapped.naam) {
      errors.push(`Regel ${index + 2}: naam ontbreekt`);
      return;
    }

    rows.push(mapped);
  });

  return { rows, errors };
}

function downloadTemplateCsv() {
  const content = [
    "naam,plaats,email,telefoon,adres,postcode,kvkNummer,btwNummer",
    "Voorbeeld BV,Amsterdam,info@voorbeeld.nl,+31201234567,Hoofdstraat 1,1011AA,12345678,NL001234567B01",
    "Installatie Partner,Rotterdam,office@installatiepartner.nl,+31101234567,Havenweg 22,3011AB,87654321,NL009876543B02",
  ].join("\n");

  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "klanten-import-template.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function ClientImportModal({ open, loading, onClose, onImport }) {
  const [strategy, setStrategy] = useState("skip");
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);

  const previewRows = useMemo(() => parsedRows.slice(0, 8), [parsedRows]);

  if (!open) return null;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsed = await parseImportFile(file, { delimiterPreference: "," });
      const mapped = mapParsedRows(parsed.headers, parsed.rows);
      setParsedRows(mapped.rows);
      setParseErrors(mapped.errors);
    } catch (error) {
      setParsedRows([]);
      setParseErrors([error?.message || "Bestand kon niet worden verwerkt."]);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    await onImport({ rows: parsedRows, strategy });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">Klanten importeren (CSV)</h3>
        <p className="mt-1 text-sm text-slate-500">
          Ondersteunde bestanden: CSV en XLSX. Kolommen: naam/bedrijfsnaam, plaats, email, telefoon, adres, postcode, kvkNummer, btwNummer.
        </p>
        <button
          type="button"
          onClick={downloadTemplateCsv}
          className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          CSV-template downloaden
        </button>

        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">CSV-bestand</label>
              <input
                type="file"
                accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFile}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Dubbele behandeling</label>
              <select
                value={strategy}
                onChange={(event) => setStrategy(event.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                {STRATEGIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {parseErrors.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {parseErrors.slice(0, 5).map((error) => (
                <p key={error}>{error}</p>
              ))}
              {parseErrors.length > 5 && <p>... en {parseErrors.length - 5} meer</p>}
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-700">Geldige rijen: {parsedRows.length}</p>
            {previewRows.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500">
                      <th className="px-2 py-1">Naam</th>
                      <th className="px-2 py-1">Plaats</th>
                      <th className="px-2 py-1">E-mail</th>
                      <th className="px-2 py-1">Telefoon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, index) => (
                      <tr key={`${row.naam}-${index}`} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-2 py-1">{row.naam}</td>
                        <td className="px-2 py-1">{row.plaats || "-"}</td>
                        <td className="px-2 py-1">{row.email || "-"}</td>
                        <td className="px-2 py-1">{row.telefoon || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm" disabled={loading}>
              Annuleren
            </button>
            <button
              type="submit"
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
              disabled={loading || parsedRows.length === 0}
            >
              {loading ? "Importeren..." : "Import starten"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
